// src/utils/slotHolds.js
// Combina los holds de horario (slot_locks) con los slots ya ocupados,
// en la forma { [date]: { [rangeLabel]: true } } que consume useAvailability.

import { formatMinutes, parseRange, intervalsOverlap } from './slotOverlap';

// Los holds se siguen mostrando bloqueados un rato después de vencer: el reloj
// del cliente puede adelantarse al del servidor, y errar hacia "ocupado" es
// preferible a ofrecer un horario que el backend va a rechazar con 409.
const DEFAULT_TOLERANCE_MS = 60_000;

// Unica fuente de la aritmetica de vencimiento + tolerancia: la usan tanto
// mergeHoldsIntoReservas (que arma el mapa de horarios ocupados) como
// hasActiveHoldWithinTolerance (que decide si vale la pena seguir recalculando
// periodicamente). Si vencen criterios distintos en cada lugar, un hold podria
// contarse como "todavia bloqueando" para uno y no para el otro.
function isHoldWithinTolerance(hold, nowMs, toleranceMs) {
  if (!hold || typeof hold.start !== 'number' || typeof hold.end !== 'number') return false;
  if (hold.end <= hold.start) return false;

  const expiresAtMs =
    hold.expiresAt && typeof hold.expiresAt.toMillis === 'function'
      ? hold.expiresAt.toMillis()
      : 0;
  return expiresAtMs > nowMs - toleranceMs;
}

export function mergeHoldsIntoReservas(reservas, lockDocs, nowMs, toleranceMs = DEFAULT_TOLERANCE_MS) {
  const result = {};
  for (const [date, slots] of Object.entries(reservas || {})) {
    result[date] = { ...slots };
  }

  if (!Array.isArray(lockDocs)) return result;

  for (const lockDoc of lockDocs) {
    if (!lockDoc || typeof lockDoc.date !== 'string' || !lockDoc.date) continue;
    const holds = lockDoc.holds;
    if (!holds || typeof holds !== 'object') continue;

    for (const hold of Object.values(holds)) {
      if (!isHoldWithinTolerance(hold, nowMs, toleranceMs)) continue;

      const label = `${formatMinutes(hold.start)} - ${formatMinutes(hold.end)}`;
      result[lockDoc.date] = result[lockDoc.date] || {};
      result[lockDoc.date][label] = true;
    }
  }

  return result;
}

// Responde si, entre todos los docs de slot_locks recibidos, queda al menos un
// hold todavia dentro de la tolerancia en el instante `nowMs`. Se usa para
// apagar el polling de 30s de useReservations en cuanto ya no hay ningun hold
// visible: sin esto el intervalo corre para siempre mientras exista CUALQUIER
// documento de holds, aunque todos sus holds hayan vencido hace horas (el
// barrido nocturno es lo unico que los borra).
export function hasActiveHoldWithinTolerance(lockDocs, nowMs, toleranceMs = DEFAULT_TOLERANCE_MS) {
  if (!Array.isArray(lockDocs)) return false;

  for (const lockDoc of lockDocs) {
    if (!lockDoc || typeof lockDoc.date !== 'string' || !lockDoc.date) continue;
    const holds = lockDoc.holds;
    if (!holds || typeof holds !== 'object') continue;

    for (const hold of Object.values(holds)) {
      if (isHoldWithinTolerance(hold, nowMs, toleranceMs)) return true;
    }
  }

  return false;
}

// Responde si el rango candidato (el turno que el profesional esta por cargar
// a mano) se solapa con algun rango retenido del dia, ya combinados por
// mergeHoldsIntoReservas. Es un aviso informativo nada mas: no bloquea nada,
// solo permite mostrarle al profesional que ese horario puntual tiene un pago
// en curso antes de que guarde una cita encima.
export function holdsOverlapRange(heldLabels, candidateLabel, fallbackDurationMin = 0) {
  const candidate = parseRange(candidateLabel, fallbackDurationMin);
  if (!candidate || !heldLabels || typeof heldLabels !== 'object') return false;

  for (const label of Object.keys(heldLabels)) {
    const held = parseRange(label);
    if (held && intervalsOverlap(candidate, held)) return true;
  }
  return false;
}

// La fecha de un slot_lock es SIEMPRE local: nace de useAvailability
// (format(d,'yyyy-MM-dd') de date-fns) y viaja sin transformarse hasta Firestore.
// toISOString() daria la fecha en UTC y, en Uruguay (UTC-3), entre las 21:00 y
// las 23:59 locales devolveria la de manana, excluyendo del filtro los holds de hoy.
export function localDateISO(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}
